import { ErrorSidePanel } from '@/components/errors';
import {
  useCommitAssociation,
  useSendAccountAssociationCode,
} from '@/hooks/api/account-association.hooks';
import { loadHistoryState } from '@/lib';
import { useForm } from '@deadcow-enterprises/react-forms';
import {
  ErrorCodes,
  templates,
  type NOSIDError,
  type SkinPageConfig,
} from '@nosinovacao/nosid-mfe-common';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { type AxiosError } from 'axios';
import { codeInputStateSchema, type SuccessState } from '../-model/flow';
import type { CodeInputProps } from '@/components/code-input/props';
import { CodeInputLight } from '@/components/code-input/code-input.light';

export const Route = createFileRoute('/(services)/services/_sheet/switch-services/code-input/')({
  loader: loadHistoryState(codeInputStateSchema, { errorCode: ErrorCodes.AccessDenied }),
  component: RouteComponent,
  errorComponent: ErrorSidePanel,
});

function RouteComponent() {
  const { associationType, selectedContact, contactId } = Route.useLoaderData();
  const navigate = useNavigate();
  const { mutateAsync: switchAssociation } = useCommitAssociation();
  const {
    mutateAsync: sendCode,
    captcha,
    isPending: resendLoading,
  } = useSendAccountAssociationCode();

  const form = useForm({
    code: {
      value: '',
    },
  });

  function handleResend() {
    if (captcha.challenge && !captcha.token) return;
    sendCode({
      captcha,
      contactId,
      targetId: selectedContact,
      type: associationType,
    });
  }

  async function handleSubmit({ code }: { code: string }) {
    try {
      const result = await switchAssociation({
        code,
        contactId,
        targetId: selectedContact,
        type: associationType,
      });
      const state: SuccessState = { services: result };
      navigate({ to: '/services/switch-services/success', state });
    } catch (err) {
      const errorCode = (err as AxiosError<NOSIDError>).response?.data?.ErrorCode;
      if (errorCode !== ErrorCodes.InvalidSecurityCode) return;
      form.addError('code', 'Services.AddServices.CodeInput.Errors.InvalidSecurityCode');
    }
  }

  const props: CodeInputProps = {
    codeValue: form.value.code,
    codeId: form.ids.code ?? 'validate-contact-otp',
    codeErrors: form.errors.code ?? [],
    isValidating: form.loading,
    isSending: resendLoading,
    captchaChallenge: captcha.challenge,
    onCaptchaChange: captcha.setToken,
    onCodeChange: (v) => form.setValue('code', v),
    onResend: handleResend,
    onSubmit: form.handleSubmit(handleSubmit),
  };
  return templates.getSkinSelector(skinConfig(props));
}

function skinConfig(props: CodeInputProps): SkinPageConfig {
  return {
    skins: [],
    defaultValue: (
      <CodeInputLight
        {...props}
        translations={{
          resendCode: 'Services.AddServices.CodeInput.Resend',
          confirm: 'Services.AddServices.CodeInput.Submit',
        }}
      />
    ),
  };
}
